/**
 * 用户中心外壳：顶部栏 + 侧边导航（移动端为横向 tab）+ 子路由出口。
 * 旧地址 /user 会重定向到 /user/overview，保持历史链接可用。
 */

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  LayoutDashboard,
  ReceiptText,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react';
import { useSiteName } from '../../platform/useSiteName';
import { getActiveToken } from '../../utils/authSession';

const NAV_ITEMS = [
  { to: '/user/overview', label: '概览', icon: LayoutDashboard },
  { to: '/user/credits', label: '积分明细', icon: ReceiptText },
  { to: '/user/invite', label: '邀请推广', icon: Users },
  { to: '/user/settings', label: '账户设置', icon: Settings },
];

export default function UserCenterLayout() {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const isLoggedIn = !!getActiveToken();

  return (
    <div className="min-h-screen bg-light-bg">
      <header className="bg-white border-b border-light-border sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors shrink-0"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold truncate">{siteName}</span>
              </button>
              <span className="text-light-muted">/</span>
              <span className="font-medium text-light-text shrink-0">用户中心</span>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm text-light-secondary hover:text-accent-blue transition-colors shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              返回
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {!isLoggedIn ? (
          <div className="bg-white border border-light-border rounded-2xl shadow-sm p-10 text-center">
            <h2 className="text-lg font-semibold text-light-text">请先登录</h2>
            <p className="text-sm text-light-muted mt-2">登录后可查看积分、消费明细与邀请返利</p>
            <button
              onClick={() => navigate('/')}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-xl text-sm hover:opacity-90 transition-opacity"
            >
              返回首页登录
            </button>
          </div>
        ) : (
          <>
            <nav className="lg:hidden -mx-1 px-1 mb-4 flex gap-2 overflow-x-auto">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-accent-blue text-white border-accent-blue'
                        : 'bg-white text-light-secondary border-light-border'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="lg:grid lg:grid-cols-[200px,1fr] lg:gap-8">
              <aside className="hidden lg:block">
                <nav className="sticky top-24 space-y-1">
                  {NAV_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                          isActive
                            ? 'bg-accent-blue/10 text-accent-blue font-medium'
                            : 'text-light-secondary hover:bg-light-hover hover:text-light-text'
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </aside>

              <main className="min-w-0 space-y-4 lg:space-y-6">
                <Outlet />
              </main>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
